/**
 * Phase 127 — In-app preview for Decks & Worksheets.
 *
 * A read-only preview surface a teacher uses to decide whether to
 * export as-is or take the file into Canva for editing. The preview
 * renders from the SAME source as the export pipeline so the two can
 * never drift apart:
 *
 *   • Worksheet — fetches the existing PDF blob from
 *     `/api/activities/:id/export?format=worksheet` and renders it via
 *     react-pdf (so we control the toolbar / chrome on mobile).
 *   • Deck — fetches the structured `slide_deck_spec` JSON via
 *     `/api/activities/:id/slide-spec` (the same JSON the PPTX builder
 *     reads from) and renders branded HTML thumbnails.
 *
 * Each preview ends with two next-step actions:
 *   • "Looks good — Export"  → triggers the existing export endpoint
 *   • "Open Canva to edit"   → downloads the import-ready file AND
 *                              opens canva.com in a new tab, then
 *                              fires a 3-step toast walking the
 *                              teacher through the upload-into-Canva
 *                              flow (Canva has no public import API).
 *
 * Each action records a `preview_action` event (no content, no student
 * data — just the teacher's choice + artifact type) so we can learn
 * how often the output is used as-is vs needs editing.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import * as React from "react";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  Layers,
  Loader2,
  Presentation,
  X,
} from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import { toast } from "sonner";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { api, getSlideSpec, logPreviewEvent } from "../lib/api";
import { AtomLoader, GENERATOR_LOADER_MESSAGES } from "./AtomLoader";

// Phase 127 — Host the pdfjs worker as a stable static asset under
// /public/ rather than via `new URL(..., import.meta.url)`. CRA's
// dynamic-chunk hash for the worker file changes per build and was
// 404ing in production on Android Chrome.
//
// Use the `.js` extension (not `.mjs`) so production servers serve
// the file with `application/javascript` Content-Type. Production
// returns `.mjs` as `application/octet-stream`, which browsers
// refuse to execute as an ES module ("Failed to fetch dynamically
// imported module"). The file contents are identical to the
// upstream `pdf.worker.min.mjs`.
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const CANVA_URL = "https://www.canva.com/";

// Phase 135.2 — Walk the teacher through the upload-into-Canva flow.
// Canva has no public deep-link import API, so the actual handoff is
// "Atomic Edu hands you the file → you upload it inside Canva." The
// toast makes that explicit instead of just dropping a vague "your
// file is ready" message.
const showCanvaStepsToast = (toastId = undefined) => {
  toast.info("Canva is ready — 3 quick steps", {
    id: toastId,
    description: (
      <ol
        data-testid="canva-steps-toast"
        className="list-decimal pl-4 space-y-1 mt-1"
      >
        <li>Canva just opened in a new tab.</li>
        <li>Your file is in your Downloads folder.</li>
        <li>
          In Canva: tap <strong>Create a design → Upload</strong>, then drop
          the file in.
        </li>
      </ol>
    ),
    duration: 12000,
  });
};

const sanitize = (name) =>
  (name || "activity").replace(/[^A-Za-z0-9_-]+/g, "_").replace(/^_+|_+$/g, "") ||
  "activity";

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1500);
};

// Small error boundary so a PDF parse failure inside <Document> falls
// back to the inline error block instead of unmounting the whole
// Preview pane (Phase 127 — react-pdf v10 crash on bad input).
class PdfErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch() {
    /* swallowed — fallback already covers the user-facing message */
  }
  render() {
    if (this.state.error) {
      return this.props.fallback || null;
    }
    return this.props.children;
  }
}

// ============================================================
// Worksheet preview — native PDF rendered via react-pdf
// ============================================================

export const WorksheetPreview = ({ activityId, activityName }) => {
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfData, setPdfData] = useState(null); // ArrayBuffer for <Document>
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [width, setWidth] = useState(640);
  const containerRef = useRef(null);

  // Fetch the consolidated Student Sheets PDF (handout + exit ticket)
  // — the SAME artifact that's bundled into the Complete Lesson
  // Package and that teachers actually print for students. The
  // `worksheet` format renders the Transferable Skills sheet, which
  // teachers consistently say is great and doesn't need a preview.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api
      .get(`/activities/${encodeURIComponent(activityId)}/export`, {
        params: { format: "student_sheets" },
        responseType: "blob",
        timeout: 90000,
      })
      .then(async (res) => {
        if (cancelled) return;
        setPdfBlob(res.data);
        // react-pdf v10 requires ArrayBuffer / Uint8Array / string for
        // the `file.data` prop — a raw Blob throws "Invalid PDF binary
        // data". Convert once here so the Document mount is clean.
        const buf = await res.data.arrayBuffer();
        if (!cancelled) setPdfData(buf);
      })
      .catch(async (e) => {
        if (cancelled) return;
        let detail = e?.response?.data?.detail || e?.message;
        if (
          e?.response?.data instanceof Blob &&
          e.response.data.type?.includes("application/json")
        ) {
          try {
            const text = await e.response.data.text();
            detail = JSON.parse(text)?.detail || text;
          } catch {
            /* leave detail */
          }
        }
        setErr(String(detail || "Couldn't load the preview."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activityId]);

  // Track container width so PDF pages scale to fit on mobile.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const obs = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) setWidth(Math.max(280, Math.min(rect.width - 24, 900)));
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const onExport = async () => {
    logPreviewEvent(activityId, { action: "export", artifact: "worksheet" });
    if (!pdfBlob) return;
    downloadBlob(pdfBlob, `${sanitize(activityName)}_StudentSheets.pdf`);
    toast.success("Student sheets downloaded");
  };

  const onOpenInCanva = async () => {
    logPreviewEvent(activityId, { action: "canva", artifact: "worksheet" });
    if (!pdfBlob) {
      toast.error("Preview isn't ready yet — try again in a moment.");
      return;
    }
    downloadBlob(pdfBlob, `${sanitize(activityName)}_StudentSheets.pdf`);
    window.open(CANVA_URL, "_blank", "noopener,noreferrer");
    showCanvaStepsToast();
  };

  // react-pdf v10 — `file` must be ArrayBuffer / Uint8Array / string,
  // NOT a Blob. We convert once on fetch (above) and pass the buffer
  // here; the Document is memo-stable so it doesn't re-parse on every
  // render.
  const fileProp = useMemo(() => (pdfData ? { data: pdfData } : null), [pdfData]);

  return (
    <section
      ref={containerRef}
      data-testid="worksheet-preview"
      className="rounded-xl border border-[#4a6984]/15 bg-white overflow-hidden"
    >
      <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#4a6984]/10 bg-[#f4f5f7]">
        <div className="inline-flex items-center gap-2 text-[#1c2d3d] font-extrabold">
          <FileText className="h-4 w-4" />
          <span>Student sheets preview</span>
        </div>
        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
          Handout + exit ticket
        </span>
      </header>

      <div className="flex flex-col items-center px-2 py-4 bg-[#1c2d3d]/[0.03] min-h-[260px]">
        {loading && (
          <div className="flex items-center gap-2 text-[#4a6984] py-12">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Rendering preview…</span>
          </div>
        )}
        {!loading && err && (
          <div
            data-testid="worksheet-preview-error"
            className="text-sm text-[#a23a3a] py-10 text-center px-6"
          >
            {err}
          </div>
        )}
        {!loading && !err && fileProp && (
          <PdfErrorBoundary
            fallback={
              <div
                data-testid="worksheet-preview-error"
                className="text-sm text-[#a23a3a] py-10 text-center px-6"
              >
                Couldn&apos;t render this student-sheets PDF in the
                browser. Use the Export button below to download it
                instead.
              </div>
            }
          >
            <Document
              file={fileProp}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              onLoadError={(e) => setErr(e?.message || "Failed to render PDF.")}
              loading={
                <div className="flex items-center gap-2 text-[#4a6984] py-12">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Rendering preview…</span>
                </div>
              }
            >
              {numPages
                ? Array.from({ length: numPages }, (_, i) => (
                    <Page
                      key={`p-${i}`}
                      pageNumber={i + 1}
                      width={width}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                      className="mb-4 shadow-md ring-1 ring-[#1c2d3d]/10"
                    />
                  ))
                : null}
            </Document>
          </PdfErrorBoundary>
        )}
      </div>

      <PreviewActions
        onExport={onExport}
        onCanva={onOpenInCanva}
        disabled={loading || !!err}
        testidPrefix="worksheet-preview"
      />
    </section>
  );
};

// ============================================================
// Deck preview — HTML thumbnails rendered from slide_deck_spec
// ============================================================

const SLIDE_TYPE_ICON = {
  title: "title",
  big_idea: "big idea",
  walt: "WALT",
  success_criteria: "success criteria",
  vocabulary: "vocabulary",
  concept: "concept",
  worked_example: "worked example",
  guided_practice: "guided practice",
  independent_practice: "independent practice",
  discussion: "discussion",
  activity: "activity",
  career_connection: "career",
  reading: "reading",
  exit_ticket: "exit ticket",
  take_up: "take-up",
  preview: "preview",
};

const SlideCard = ({ slide, index, onOpen, total }) => {
  const label = SLIDE_TYPE_ICON[slide.type] || slide.type || "slide";
  return (
    <button
      type="button"
      data-testid={`deck-preview-thumb-${index}`}
      onClick={() => onOpen(index)}
      className="relative shrink-0 w-64 sm:w-72 aspect-video rounded-lg border border-[#4a6984]/15 bg-white overflow-hidden text-left hover:border-[#1c2d3d]/40 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#96ded1] transition-all"
    >
      <SlideBody slide={slide} compact />
      <div className="absolute bottom-1.5 left-2 right-2 flex items-center justify-between">
        <span className="text-[9px] font-extrabold uppercase tracking-[0.18em] text-[#1c2d3d]/55 bg-white/85 px-1.5 py-0.5 rounded">
          {label}
        </span>
        <span className="text-[9px] font-extrabold uppercase tracking-[0.18em] text-white bg-[#1c2d3d]/85 px-1.5 py-0.5 rounded">
          {index + 1} / {total}
        </span>
      </div>
    </button>
  );
};

// Branded slide body — navy/mint palette + Montserrat (matches the
// PPTX export's visual treatment). `compact` shrinks type for thumbs.
const SlideBody = ({ slide, compact = false }) => {
  const titleSize = compact ? "text-[11px]" : "text-2xl sm:text-3xl";
  const bodySize = compact ? "text-[9px]" : "text-base sm:text-lg";
  const padding = compact ? "p-3" : "p-8 sm:p-12";
  const title = slide.title || "";
  const bullets = Array.isArray(slide.bullets) ? slide.bullets : [];
  const body = slide.body || slide.callout || slide.subtitle || "";
  const isTitle = slide.type === "title" || slide.type === "big_idea";

  return (
    <div
      className={`w-full h-full flex flex-col ${padding} ${
        isTitle
          ? "bg-gradient-to-br from-[#1c2d3d] to-[#2a3f55] text-white"
          : "bg-white text-[#1c2d3d]"
      }`}
    >
      <div className={`font-extrabold leading-tight ${titleSize}`}>
        {title}
      </div>
      {!compact && (
        <div className="h-1 w-12 bg-[#96ded1] my-3 rounded-full" aria-hidden />
      )}
      {compact && (
        <div className="h-[2px] w-6 bg-[#96ded1] my-1.5 rounded-full" aria-hidden />
      )}
      {bullets.length > 0 ? (
        <ul className={`${bodySize} space-y-1 leading-snug ${compact ? "" : "sm:space-y-2"}`}>
          {bullets.slice(0, compact ? 3 : 8).map((b, i) => (
            <li key={i} className="flex gap-1.5">
              <span className="text-[#96ded1] font-extrabold">•</span>
              <span className="line-clamp-2">{b}</span>
            </li>
          ))}
        </ul>
      ) : (
        body && (
          <p className={`${bodySize} leading-snug ${compact ? "line-clamp-4" : ""}`}>
            {body}
          </p>
        )
      )}
      {slide.time && !compact && (
        <div className="mt-auto pt-4 text-xs font-extrabold uppercase tracking-[0.18em] text-[#4a6984]/70">
          {slide.time}
        </div>
      )}
    </div>
  );
};

export const DeckPreview = ({ activityId, activityName }) => {
  const [slides, setSlides] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    getSlideSpec(activityId)
      .then((spec) => {
        if (cancelled) return;
        setSlides(spec?.slides || []);
      })
      .catch((e) => {
        if (cancelled) return;
        const detail =
          e?.response?.data?.detail || e?.message || "Couldn't generate preview.";
        setErr(String(detail));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activityId]);

  // Keyboard nav inside the lightbox.
  useEffect(() => {
    if (lightboxIdx === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIdx(null);
      else if (e.key === "ArrowLeft")
        setLightboxIdx((i) => (i === null ? null : Math.max(0, i - 1)));
      else if (e.key === "ArrowRight")
        setLightboxIdx((i) =>
          i === null ? null : Math.min((slides?.length || 1) - 1, i + 1),
        );
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, slides]);

  const onExport = async () => {
    logPreviewEvent(activityId, { action: "export", artifact: "deck" });
    const toastId = toast.loading("Building your slide deck (.pptx)…");
    try {
      const res = await api.get(
        `/activities/${encodeURIComponent(activityId)}/export`,
        {
          params: { format: "slides" },
          responseType: "blob",
          timeout: 120000,
        },
      );
      downloadBlob(res.data, `${sanitize(activityName)}_StudentDeck.pptx`);
      toast.success("Slide deck downloaded", { id: toastId });
    } catch (e) {
      toast.error("Couldn't download deck", {
        id: toastId,
        description: String(e?.response?.data?.detail || e?.message || ""),
      });
    }
  };

  const onOpenInCanva = async () => {
    logPreviewEvent(activityId, { action: "canva", artifact: "deck" });
    const toastId = toast.loading("Preparing your .pptx for Canva…");
    try {
      const res = await api.get(
        `/activities/${encodeURIComponent(activityId)}/export`,
        {
          params: { format: "slides" },
          responseType: "blob",
          timeout: 120000,
        },
      );
      downloadBlob(res.data, `${sanitize(activityName)}_StudentDeck.pptx`);
      window.open(CANVA_URL, "_blank", "noopener,noreferrer");
      showCanvaStepsToast(toastId);
    } catch (e) {
      toast.error("Couldn't prepare deck for Canva", {
        id: toastId,
        description: String(e?.response?.data?.detail || e?.message || ""),
      });
    }
  };

  return (
    <section
      data-testid="deck-preview"
      className="rounded-xl border border-[#4a6984]/15 bg-white overflow-hidden"
    >
      <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#4a6984]/10 bg-[#f4f5f7]">
        <div className="inline-flex items-center gap-2 text-[#1c2d3d] font-extrabold">
          <Presentation className="h-4 w-4" />
          <span>Student deck preview</span>
        </div>
        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
          {slides ? `${slides.length} slides` : "Same source as your export"}
        </span>
      </header>

      <div className="px-4 py-4 bg-[#1c2d3d]/[0.03] min-h-[180px]">
        {loading && (
          <div
            data-testid="deck-preview-loading"
            className="flex items-center justify-center py-6"
          >
            <AtomLoader
              size="sm"
              messages={GENERATOR_LOADER_MESSAGES}
              sublabel="First run can take ~20s."
            />
          </div>
        )}
        {!loading && err && (
          <div
            data-testid="deck-preview-error"
            className="text-sm text-[#a23a3a] py-10 text-center px-6"
          >
            {err}
          </div>
        )}
        {!loading && !err && slides && slides.length === 0 && (
          <div className="text-sm text-[#4a6984] py-10 text-center px-6">
            This activity doesn&apos;t have a slide deck.
          </div>
        )}
        {!loading && !err && slides && slides.length > 0 && (
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
            {slides.map((s, i) => (
              <div key={i} className="snap-start">
                <SlideCard
                  slide={s}
                  index={i}
                  onOpen={setLightboxIdx}
                  total={slides.length}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <PreviewActions
        onExport={onExport}
        onCanva={onOpenInCanva}
        disabled={loading || !!err || !(slides && slides.length)}
        testidPrefix="deck-preview"
      />

      {lightboxIdx !== null && slides && slides[lightboxIdx] && (
        <DeckLightbox
          slides={slides}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onPrev={() => setLightboxIdx(Math.max(0, lightboxIdx - 1))}
          onNext={() =>
            setLightboxIdx(Math.min(slides.length - 1, lightboxIdx + 1))
          }
        />
      )}
    </section>
  );
};

const DeckLightbox = ({ slides, index, onClose, onPrev, onNext }) => {
  const slide = slides[index];
  return (
    <div
      data-testid="deck-preview-lightbox"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-[#1c2d3d]/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={onClose}
    >
      <button
        type="button"
        aria-label="Close preview"
        data-testid="deck-preview-lightbox-close"
        onClick={onClose}
        className="absolute top-3 right-3 sm:top-5 sm:right-5 h-9 w-9 rounded-full bg-white/95 hover:bg-white text-[#1c2d3d] inline-flex items-center justify-center shadow-lg"
      >
        <X className="h-5 w-5" />
      </button>
      <button
        type="button"
        aria-label="Previous slide"
        data-testid="deck-preview-lightbox-prev"
        disabled={index === 0}
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        className="absolute left-3 sm:left-6 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white/95 hover:bg-white text-[#1c2d3d] inline-flex items-center justify-center shadow-lg disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        type="button"
        aria-label="Next slide"
        data-testid="deck-preview-lightbox-next"
        disabled={index === slides.length - 1}
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        className="absolute right-3 sm:right-6 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white/95 hover:bg-white text-[#1c2d3d] inline-flex items-center justify-center shadow-lg disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <ChevronRight className="h-6 w-6" />
      </button>
      <div
        className="relative w-full max-w-4xl aspect-video rounded-xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <SlideBody slide={slide} />
      </div>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/90 text-xs font-extrabold uppercase tracking-[0.18em]">
        Slide {index + 1} of {slides.length}
      </div>
    </div>
  );
};

// ============================================================
// Shared CTA row
// ============================================================

const PreviewActions = ({ onExport, onCanva, disabled, testidPrefix }) => (
  <footer className="flex flex-col sm:flex-row items-stretch gap-2 px-4 py-3 border-t border-[#4a6984]/10 bg-white">
    <button
      type="button"
      data-testid={`${testidPrefix}-export-btn`}
      onClick={onExport}
      disabled={disabled}
      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-extrabold text-sm bg-[#1c2d3d] text-white hover:bg-[#2a3f55] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      <Download className="h-4 w-4" />
      Looks good — Export
    </button>
    <div className="flex flex-col gap-1">
      <button
        type="button"
        data-testid={`${testidPrefix}-canva-btn`}
        onClick={onCanva}
        disabled={disabled}
        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-extrabold text-sm border-2 border-[#96ded1] bg-[#96ded1]/25 text-[#1c2d3d] hover:bg-[#96ded1]/45 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <Layers className="h-4 w-4" />
        Open Canva to edit
        <ExternalLink className="h-3.5 w-3.5 opacity-70" />
      </button>
      <span className="text-[11px] text-[#4a6984]/80 leading-tight sm:max-w-[14rem]">
        Canva will open, document will download — upload to Canva to edit.
      </span>
    </div>
  </footer>
);
